import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useProcessApiClient } from '@/api/clients/useProcessApiClient.util.ts';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';

type Req = {
  multiProcessOfferGroupId: string;
  offerPrintId: number;
  processId: string;
  processOfferId?: string;
};

type Return = {
  isUpdating: boolean;
  updateOfferPrintTemplate: (req: Req) => Promise<void>;
};

export const useUpdateOfferPrintTemplate = (): Return => {
  const { processOfferFileGenerator } = useProcessApiClient();
  const { userId } = useUserContext();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    mutationFn: async req => {
      await processOfferFileGenerator.updateOfferPrintTemplate(
        userId,
        req.processId,
        req.multiProcessOfferGroupId,
        { offerPrintId: req.offerPrintId },
        req.processOfferId,
      );
    },
    mutationKey: [QueryKeyEnum.OFFER_PRINTS.ASSIGN_TO_OFFER],
    onSuccess: async (_data, req) => {
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: [QueryKeyEnum.PROCESS.MULTI_OFFER_DETAILS, req.multiProcessOfferGroupId],
        }),
        ...(req.processOfferId
          ? [
              queryClient.refetchQueries({
                queryKey: [QueryKeyEnum.PROCESS.OFFER_DETAILS, req.processOfferId],
              }),
            ]
          : []),
      ]);
    },
  });

  return {
    isUpdating: isPending,
    updateOfferPrintTemplate: mutateAsync,
  };
};
